"use client";

import { useEffect, useRef } from "react";

/**
 * Creates a stable facade object for an imperative ref target that may not yet be ready.
 * - Any method calls made before the target exists are queued and replayed when the target becomes available.
 * - Non-function property access is passed through when available; otherwise undefined.
 * - You can expose additional fields via `expose` (values or getters via functions) that are always available.
 *
 * @param {React.MutableRefObject<any>} targetRef - Ref to the eventual target instance.
 * @param {Record<string, any | (() => any)>} [expose] - Optional map of properties to expose on the facade.
 *        If a value is a function, it will be called at access time to retrieve the latest value.
 * @param {any[]} [deps] - Dependency array that, when changed, will trigger flushing of queued calls.
 * @returns {any} Stable proxy facade object.
 */
export default function useDeferredFacade(
  targetRef,
  expose = {},
  deps = [],
  knownMethods = []
) {
  const callQueue = useRef([]);
  const facadeRef = useRef(null);

  if (!facadeRef.current) {
    facadeRef.current = new Proxy(
      {},
      {
        get: (_, prop) => {
          // Exposed properties (value or getter)
          if (Object.prototype.hasOwnProperty.call(expose, prop)) {
            const exposed = expose[prop];
            return typeof exposed === "function" ? exposed() : exposed;
          }

          // Pass-through to target if available
          const inner = targetRef.current;
          const value = inner ? inner[prop] : undefined;
          if (typeof value === "function") {
            // Wrap methods to defer if target not yet ready
            return (...args) => {
              const current = targetRef.current;
              const method = current ? current[prop] : undefined;
              if (typeof method === "function") {
                return method(...args);
              }
              callQueue.current.push([prop, args]);
              return undefined;
            };
          }

          // If target not ready yet and this is a known imperative method,
          // return a stub that queues the call so optional chaining works.
          if (!inner && knownMethods.includes(prop)) {
            return (...args) => {
              callQueue.current.push([prop, args]);
              return undefined;
            };
          }
          return value;
        },
        has: () => true,
      }
    );
  }

  function flushQueueIfReady() {
    if (!targetRef.current || callQueue.current.length === 0) return;
    const queued = callQueue.current.slice();
    callQueue.current.length = 0;
    queued.forEach(([name, args]) => {
      const fn = targetRef.current?.[name];
      if (typeof fn === "function") fn(...args);
    });
  }

  // Public attach helper to set the target and immediately flush
  const attach = (instance) => {
    targetRef.current = instance;
    flushQueueIfReady();
  };

  // Flush queued calls whenever deps indicate readiness change
  useEffect(() => {
    flushQueueIfReady();
  }, deps);

  return { facade: facadeRef.current, attach };
}
